import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import ShareButton from "../components/ShareButton";
import { fetchContentById } from "../services/ContentService";
import { API_BASE } from "../Config";
import ContentData from "../models/ContentData";
import { IMAGE_BASE } from "../Config";
import axiosInstance from "../services/BaseService";

const ArticleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<ContentData | null>(null);
  const [contentTypeName, setContentTypeName] = useState<string>("Article");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const articleData = await fetchContentById(id);
        setArticle(articleData);
        
        // Fetch content type name
        if (articleData.contentType) {
          try {
            const response = await axiosInstance.get(`/contenttypes/${articleData.contentType}`);
            setContentTypeName(response.data.name || "Article");
          } catch (err) {
            console.error("Failed to fetch content type:", err);
          }
        }
      } catch (err) {
        setError("Failed to load article");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading article...</p>
      </Container>
    );
  }

  if (error || !article) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Article Not Found</h4>
          <p>{error || "The requested article could not be found."}</p>
          <Link to="/articles" className="btn btn-warning">Back to Articles</Link>
        </Alert>
      </Container>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getBadgeColor = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'news': return 'warning';
      case 'review': return 'info';
      case 'tutorial': return 'success';
      case 'article': return 'primary';
      default: return 'secondary';
    }
  };

  const getContentTypeIcon = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'news': return 'bi-newspaper';
      case 'review': return 'bi-star-half';
      case 'tutorial': return 'bi-mortarboard';
      case 'article': return 'bi-journal-text';
      default: return 'bi-file-text';
    }
  };
  
  const getContentTypePlural = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'news': return 'news';
      case 'review': return 'reviews';
      case 'tutorial': return 'tutorials';
      case 'article': return 'articles';
      default: return `${type.toLowerCase()}s`;
    }
  };

  const getMetaDescription = () => {
    if (!article?.content) return "Read this article on Natakiya!";
    const plainText = article.content.replace(/<[^>]*>/g, '').replace(/\n/g, ' ');
    return plainText.length > 160 ? `${plainText.substring(0, 160)}...` : plainText;
  };

  const getMetaImage = () => {
   if (article?.imageLink) {
      return `${IMAGE_BASE}${article.imageLink}`;
    }
    return `${window.location.origin}/logo.png`;
  };

  return (
    <>
      <Helmet>
        <title>{article ? `${article.title} | Natakiya` : 'Article | Natakiya'}</title>
        <meta name="description" content={getMetaDescription()} />
        
        {/* Open Graph meta tags for Facebook, WhatsApp */}
        <meta property="og:title" content={article ? `${article.title} | Natakiya` : 'Article | Natakiya'} />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:image" content={getMetaImage()} />
        <meta property="og:image:secure_url" content={getMetaImage()} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={article ? article.title : 'Natakiya Article'} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Natakiya" />
        <meta property="og:locale" content="en_US" />
        
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article ? `${article.title} | Natakiya` : 'Article | Natakiya'} />
        <meta name="twitter:description" content={getMetaDescription()} />
        <meta name="twitter:image" content={getMetaImage()} />
        
        {/* Additional meta tags */}
        {article?.writer && <meta name="author" content={article.writer} />}
        <meta property="article:section" content={contentTypeName} />
        {article?.createdAt && <meta property="article:published_time" content={article.createdAt} />}
      </Helmet>
      
      <Container className="py-4">
        {/* Breadcrumb */}
      <Row className="mb-3">
        <Col xs={12}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={`/${getContentTypePlural(contentTypeName)}`} className="text-decoration-none">{getContentTypePlural(contentTypeName)}</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {article.title}
              </li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Article Header */}
      <Row className="mb-4">
        <Col xs={12}>
          <h1 className="display-5 fw-bold mb-3">{article.title}</h1>
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            {article.writer && (
              <div className="d-flex align-items-center">
                <i className="bi bi-person-circle text-warning me-2"></i>
                <span className="fw-semibold">{article.writer}</span>
              </div>
            )}
            {/* <div className="d-flex align-items-center">
              <i className="bi bi-calendar text-warning me-2"></i>
              <span>{formatDate(article.publishedDate || article.createdAt)}</span>
            </div> */}
            <Badge 
              bg={getBadgeColor(contentTypeName.toLowerCase())} 
              text={contentTypeName.toLowerCase() === "news" ? "dark" : "white"} 
              className="px-3 py-2"
            >
              <i className={`bi ${getContentTypeIcon(contentTypeName.toLowerCase())} me-1`}></i>
              {contentTypeName}
            </Badge>
          </div>
        </Col>
      </Row>

      {/* Article Image */}
      {article.imageLink && (
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Img 
                variant="top" 
                src={IMAGE_BASE + article.imageLink} 
                style={{ height: "400px", objectFit: "cover" }}
                alt={article.title}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Article Content */}
      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div 
                className="article-content"
                style={{ 
                  fontSize: "1.1rem", 
                  lineHeight: "1.8",
                  color: "#333"
                }}
                dangerouslySetInnerHTML={{ 
                  __html: article.content?.replace(/\n/g, '<br>') || "No content available." 
                }}
              />
              
              {/* Tags */}
              {/* {article.tags && article.tags.length > 0 && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-2">Tags:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {article.tags.map((tag, index) => (
                      <Badge key={index} bg="outline-secondary" text="dark">
                        #{tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )} */}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row className="mt-4">
        <Col xs={12} className="text-center">
          <div className="d-flex justify-content-center gap-3">
            <Link to={`/${getContentTypePlural(contentTypeName)}`} className="btn btn-outline-primary">
              <i className="bi bi-arrow-left me-2"></i>Back to {getContentTypePlural(contentTypeName)}
            </Link>
            <Button variant="warning" onClick={() => window.print()}>
              <i className="bi bi-printer me-2"></i>Print Article
            </Button>
            <ShareButton
              title={article.title}
              description={article.content?.substring(0, 150) || "Read this article on Natakiya!"}
              imageUrl={article.imageLink ? `${IMAGE_BASE}${article.imageLink}` : `${window.location.origin}/logo.png`}
            />
          </div>
        </Col>
      </Row>

      {/* Article Meta Info */}
      <Row className="mt-5">
        <Col xs={12}>
          <Card className="bg-light border-0">
            {/* <Card.Body>
              <Row>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Published:</strong> {formatDate(article.publishedDate || article.createdAt)}
                  </small>
                </Col>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Last Updated:</strong> {formatDate(article.updatedAt)}
                  </small>
                </Col>
              </Row>
            </Card.Body> */}
          </Card>
        </Col>
      </Row>
      </Container>
    </>
  );
};

export default ArticleDetailPage;